'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Zap, Play, Edit2, Trash2, ChevronDown, Plus, BarChart2 } from 'lucide-react';


interface AutomationRule {
  id: string;
  name: string;
  description: string;
  triggerType: 'AI Detection' | 'Threshold' | 'Manual' | 'Schedule';
  triggerCondition: string;
  targetEntity: string;
  action: string;
  enabled: boolean;
  executions: number;
  successRate: number;
  lastRun: string;
  hasRollback: boolean;
  priority: 'high' | 'medium' | 'low';
}

const rules: AutomationRule[] = [
  {
    id: 'rule-001', name: 'BGP-Session-Restart', description: 'Automatically restart BGP sessions when flap detected',
    triggerType: 'AI Detection', triggerCondition: 'BGP_SESSION_FLAP confidence ≥ 90%',
    targetEntity: 'Core/Edge Routers', action: 'soft-reset bgp all', enabled: true,
    executions: 342, successRate: 96.2, lastRun: '09:23:55', hasRollback: true, priority: 'high',
  },
  {
    id: 'rule-002', name: 'Interface-Flap-Dampen', description: 'Apply dampening to flapping interfaces above threshold',
    triggerType: 'Threshold', triggerCondition: 'Interface flap count > 5 in 10min',
    targetEntity: 'All Routers', action: 'dampening half-life 15 reuse 750 suppress 2000',
    enabled: true, executions: 218, successRate: 99.1, lastRun: '09:24:01', hasRollback: true, priority: 'high',
  },
  {
    id: 'rule-003', name: 'Traffic-Reroute-ECMP', description: 'Reroute traffic via ECMP when primary path saturated',
    triggerType: 'AI Detection', triggerCondition: 'INTERFACE_OVERLOAD confidence ≥ 85%',
    targetEntity: 'Core Routers', action: 'ip route redistribute ecmp', enabled: true,
    executions: 87, successRate: 91.8, lastRun: '09:22:18', hasRollback: true, priority: 'medium',
  },
  {
    id: 'rule-004', name: 'OSPF-Neighbor-Reset', description: 'Reset OSPF neighbor relationship on instability detection',
    triggerType: 'AI Detection', triggerCondition: 'OSPF_INSTABILITY confidence ≥ 80%',
    targetEntity: 'Aggregation Nodes', action: 'clear ip ospf neighbor', enabled: true,
    executions: 156, successRate: 88.5, lastRun: '09:21:40', hasRollback: false, priority: 'medium',
  },
  {
    id: 'rule-005', name: 'CPU-Process-Kill', description: 'Kill runaway routing process consuming excessive CPU',
    triggerType: 'Threshold', triggerCondition: 'CPU > 92% sustained for 5min',
    targetEntity: 'All Nodes', action: 'process restart routing-process', enabled: true,
    executions: 43, successRate: 74.4, lastRun: '09:20:11', hasRollback: false, priority: 'high',
  },
  {
    id: 'rule-006', name: 'QoS-Policy-Reapply', description: 'Reapply QoS policy when DSCP violation detected',
    triggerType: 'AI Detection', triggerCondition: 'QOS_VIOLATION confidence ≥ 75%',
    targetEntity: 'Edge Routers', action: 'policy-map reapply service-policy', enabled: false,
    executions: 29, successRate: 100, lastRun: '08:55:14', hasRollback: true, priority: 'low',
  },
  {
    id: 'rule-007', name: 'NTP-Resync', description: 'Force NTP resync when clock drift exceeds threshold',
    triggerType: 'Threshold', triggerCondition: 'NTP drift > 500ms',
    targetEntity: 'All Nodes', action: 'ntp update-calendar', enabled: true,
    executions: 71, successRate: 98.6, lastRun: '08:30:22', hasRollback: false, priority: 'low',
  },
  {
    id: 'rule-008', name: 'Link-Failover-Activate', description: 'Activate backup link on primary link failure',
    triggerType: 'AI Detection', triggerCondition: 'NODE_UNREACHABLE confidence ≥ 95%',
    targetEntity: 'Edge Routers', action: 'track 1 interface failover', enabled: true,
    executions: 12, successRate: 100, lastRun: '09:18:05', hasRollback: true, priority: 'high',
  },
];

const triggerColors: Record<string, string> = {
  'AI Detection': 'text-primary bg-primary/10 border-primary/20',
  'Threshold': 'text-warning bg-warning/10 border-warning/20',
  'Manual': 'text-muted bg-muted/10 border-muted/20',
  'Schedule': 'text-info bg-info/10 border-info/20',
};

const priorityDot: Record<string, string> = {
  high: 'bg-danger',
  medium: 'bg-warning',
  low: 'bg-success',
};

export default function AutomationRulesSection() {
  const [ruleStates, setRuleStates] = useState<Record<string, boolean>>(
    Object.fromEntries(rules.map((r) => [r.id, r.enabled]))
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleRule = (id: string) => {
    const next = !ruleStates[id];
    setRuleStates((prev) => ({ ...prev, [id]: next }));
    const rule = rules.find((r) => r.id === id);
    toast[next ? 'success' : 'info'](`Rule "${rule?.name}" ${next ? 'enabled' : 'paused'}`);
  };

  const successColor = (rate: number) =>
    rate >= 95 ? 'text-success' : rate >= 80 ? 'text-warning' : 'text-danger';

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Automation Rules</p>
          <span className="text-[10px] bg-success/10 text-success border border-success/20 px-2 py-0.5 rounded-full font-mono">
            {Object.values(ruleStates).filter(Boolean).length} active
          </span>
        </div>
        <button
          onClick={() => toast.info('Opening rule editor…')}
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
        >
          <Plus size={12} />
          New Rule
        </button>
      </div>

      <div className="space-y-2 max-h-[520px] overflow-y-auto scrollbar-hide">
        {rules.map((rule) => {
          const enabled = ruleStates[rule.id];
          const expanded = expandedId === rule.id;

          return (
            <div
              key={rule.id}
              className={`border rounded-lg overflow-hidden transition-all duration-200
                ${enabled ? 'border-border' : 'border-border/40 opacity-60'}
                ${expanded ? 'bg-surface-raised' : 'hover:bg-surface-raised/50'}`}
            >
              {/* Rule header */}
              <div className="flex items-center gap-3 px-3 py-2.5">
                {/* Priority dot */}
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityDot[rule.priority]}`} />

                {/* Toggle */}
                <button
                  onClick={() => toggleRule(rule.id)}
                  className={`relative w-8 h-4.5 rounded-full transition-all duration-200 flex-shrink-0 focus:outline-none
                    ${enabled ? 'bg-primary' : 'bg-surface-overlay border border-border'}`}
                  style={{ minWidth: 32, height: 18 }}
                  title={enabled ? 'Disable rule' : 'Enable rule'}
                >
                  <span
                    className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all duration-200
                      ${enabled ? 'left-[14px]' : 'left-0.5'}`}
                  />
                </button>

                {/* Name + trigger */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[12px] font-semibold text-foreground truncate">{rule.name}</p>
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide flex-shrink-0 ${triggerColors[rule.triggerType]}`}>
                      {rule.triggerType}
                    </span>
                    {rule.hasRollback && (
                      <span className="text-[9px] bg-info/10 text-info border border-info/20 px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold flex-shrink-0">
                        Rollback
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-3 text-[11px] flex-shrink-0">
                  <span className={`font-mono font-bold ${successColor(rule.successRate)}`}>
                    {rule.successRate}%
                  </span>
                  <span className="text-muted font-mono">{rule.executions.toLocaleString()}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toast.info(`Editing rule ${rule.name}…`)}
                    className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                    title="Edit rule"
                  >
                    <Edit2 size={11} />
                  </button>
                  <button
                    onClick={() => toast.info(`Running ${rule.name} manually…`)}
                    className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-primary transition-colors"
                    title="Run manually"
                  >
                    <Play size={11} />
                  </button>
                  <button
                    onClick={() => setExpandedId(expanded ? null : rule.id)}
                    className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                    title="Expand details"
                  >
                    <ChevronDown size={11} className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Expanded details */}
              {expanded && (
                <div className="px-3 pb-3 pt-0 border-t border-border space-y-2.5 animate-fade-in">
                  <p className="text-[11px] text-foreground-muted pt-2">{rule.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Trigger Condition', value: rule.triggerCondition },
                      { label: 'Target', value: rule.targetEntity },
                      { label: 'Action', value: rule.action },
                      { label: 'Last Run', value: rule.lastRun },
                    ].map((f) => (
                      <div key={`rf-${f.label}`} className="bg-surface rounded-lg p-2">
                        <p className="text-[9px] font-semibold uppercase tracking-widest text-muted mb-0.5">{f.label}</p>
                        <p className="text-[11px] font-mono text-foreground break-all">{f.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toast.error(`Deleting rule ${rule.name}…`)}
                      className="flex items-center gap-1.5 text-[11px] text-danger hover:bg-danger/10 px-2 py-1 rounded transition-colors"
                    >
                      <Trash2 size={11} />
                      Delete Rule
                    </button>
                    <button
                      onClick={() => toast.info(`Viewing execution history for ${rule.name}…`)}
                      className="flex items-center gap-1.5 text-[11px] text-muted hover:text-foreground px-2 py-1 rounded hover:bg-surface-overlay transition-colors"
                    >
                      <BarChart2 size={11} />
                      Execution History
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}